/**
 * How customers paid, for the Payments dashboard: BNPL, credit, debit and
 * FSA/HSA as shares of new checkouts, by count and by revenue, with BNPL split
 * by provider and the weekly trend. Data from /payments/dashboard/tender-mix
 * (backend services/manifoldTenderMix), the same window as the panels below.
 *
 * AMOUNTS ARRIVE IN CENTS and the page's formatCurrency takes DOLLARS, as in
 * GatewaySection and DeclinesSection.
 *
 * Colors are fixed per tender (.tender-viz in index.css, validated), so a
 * tender keeps its color in every bar and week.
 */

import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from 'recharts'
import { Card, CardHeader, CardTitle, CardContent } from './ui'

const colorOf = key => `var(--tender-${key})`
const pct = v => (v == null ? '—' : v > 0 && v < 0.01 ? '<1%' : `${Math.round(v * 100)}%`)
const pct1 = v => (v == null ? '—' : `${(v * 100).toFixed(1)}%`)
const money = (cents, format) => format((Number(cents) || 0) / 100)

/** One 100% bar, segments labelled inside when they are wide enough. */
function ShareBar({ label, tenders, field }) {
  return (
    <div>
      <div className="text-xs text-muted-foreground mb-1">{label}</div>
      <div className="flex h-8 w-full overflow-hidden rounded-md">
        {tenders.filter(t => t[field] > 0).map((t, i) => (
          <div
            key={t.key}
            className="flex items-center justify-center text-xs font-medium text-white"
            style={{
              width: `${t[field] * 100}%`,
              background: colorOf(t.key),
              // A 2px surface gap between segments.
              borderLeft: i ? '2px solid hsl(var(--card))' : undefined,
            }}
            title={`${t.label}: ${pct1(t[field])}`}
          >
            {t[field] >= 0.07 ? `${t.label} ${pct(t[field])}` : ''}
          </div>
        ))}
      </div>
    </div>
  )
}

function WeekTooltip({ active, payload, tenders }) {
  if (!active || !payload?.length) return null
  const w = payload[0].payload
  return (
    <div className="rounded-md border bg-popover px-3 py-2 text-xs shadow-md">
      <div className="font-medium mb-1">Week of {w.week} · {w.sales} sales</div>
      {tenders.map(t => (
        <div key={t.key} className="flex justify-between gap-6">
          <span className="inline-flex items-center gap-1.5"><span className="h-2 w-2 rounded-sm" style={{ background: colorOf(t.key) }} />{t.label}</span>
          <span className="tabular-nums">{pct1(w[`${t.key}Share`])}</span>
        </div>
      ))}
    </div>
  )
}

export default function TenderMixSection({ data, loading, error, formatCurrency, formatNumber = n => n.toLocaleString() }) {
  if (loading && !data) return <Card><CardContent className="py-8 text-sm text-muted-foreground">Loading payment methods…</CardContent></Card>
  if (error) return <Card><CardContent className="py-6 text-sm text-muted-foreground">Payment methods unavailable: {error}</CardContent></Card>
  if (!data || !data.sales) return <Card><CardContent className="py-6 text-sm text-muted-foreground">No new checkouts in this window.</CardContent></Card>

  const { tenders, bnplProviders, wallets = [], weekly, window } = data

  return (
    <Card className="tender-viz">
      <CardHeader className="pb-3">
        <CardTitle className="text-base">How customers paid</CardTitle>
        <p className="text-xs text-muted-foreground">
          New checkouts only ({formatNumber(data.sales)} sales, {money(data.amount, formatCurrency)}), {window?.startDate} to {window?.endDate}. Renewals are left out: they are cards on file, and BNPL never renews. FSA/HSA is a card on our list of known benefit-card BINs or labelled a benefits card by its network; those are debit cards and would otherwise count as debit. Read it as a floor until the BIN database is in.
        </p>
      </CardHeader>
      <CardContent className="space-y-5">
        <div className="space-y-3">
          <ShareBar label="Share of sales" tenders={tenders} field="countShare" />
          <ShareBar label="Share of revenue" tenders={tenders} field="amountShare" />
        </div>

        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-xs text-muted-foreground">
                <th className="text-left py-1.5 font-medium">Paid with</th>
                <th className="text-right py-1.5 font-medium">Sales</th>
                <th className="text-right py-1.5 font-medium">Share</th>
                <th className="text-right py-1.5 font-medium">Revenue</th>
                <th className="text-right py-1.5 font-medium">Share</th>
                <th className="text-right py-1.5 font-medium">Avg order</th>
              </tr>
            </thead>
            <tbody>
              {tenders.map(t => (
                <tr key={t.key} className="border-t">
                  <td className="py-1.5">
                    <span className="inline-flex items-center gap-2">
                      <span className="h-2.5 w-2.5 rounded-sm" style={{ background: colorOf(t.key) }} />{t.label}
                    </span>
                    {t.key === 'bnpl' && bnplProviders.length > 0 && (
                      <div className="ml-[18px] text-xs text-muted-foreground">
                        {bnplProviders.map(p => `${p.label} ${pct(p.countShare)}`).join(' · ')}
                      </div>
                    )}
                  </td>
                  <td className="py-1.5 text-right tabular-nums">{formatNumber(t.count)}</td>
                  <td className="py-1.5 text-right tabular-nums font-medium">{pct1(t.countShare)}</td>
                  <td className="py-1.5 text-right tabular-nums">{money(t.amount, formatCurrency)}</td>
                  <td className="py-1.5 text-right tabular-nums font-medium">{pct1(t.amountShare)}</td>
                  <td className="py-1.5 text-right tabular-nums">{t.aov != null ? money(t.aov, formatCurrency) : '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {wallets.length > 0 && (
          <div className="text-xs text-muted-foreground">
            <span className="font-medium text-foreground">Paid through a wallet</span> (already counted in Credit/Debit above):{' '}
            {wallets.map(w => `${w.label} ${formatNumber(w.count)} sales · ${pct1(w.countShare)} of sales · ${money(w.amount, formatCurrency)} (${w.credit} credit, ${w.debit} debit)`).join('; ')}
          </div>
        )}

        {weekly.length > 1 && (
          <div>
            <div className="text-xs text-muted-foreground mb-1">Share of sales by week (first and last weeks may be partial)</div>
            <div style={{ height: 160 }}>
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={weekly} margin={{ top: 4, right: 4, left: -16, bottom: 0 }} barCategoryGap="20%">
                  <CartesianGrid vertical={false} stroke="hsl(var(--border))" />
                  <XAxis dataKey="week" tick={{ fontSize: 11, fill: 'hsl(var(--muted-foreground))' }} axisLine={false} tickLine={false} tickFormatter={w => w.slice(5)} />
                  <YAxis domain={[0, 1]} tickFormatter={v => `${Math.round(v * 100)}%`} tick={{ fontSize: 11, fill: 'hsl(var(--muted-foreground))' }} axisLine={false} tickLine={false} />
                  <Tooltip content={<WeekTooltip tenders={tenders} />} cursor={{ fill: 'hsl(var(--muted))', opacity: 0.5 }} />
                  {tenders.map((t, i) => (
                    <Bar
                      key={t.key}
                      dataKey={`${t.key}Share`}
                      stackId="w"
                      fill={colorOf(t.key)}
                      stroke="hsl(var(--card))"
                      strokeWidth={1}
                      radius={i === tenders.length - 1 ? [4, 4, 0, 0] : 0}
                      isAnimationActive={false}
                    />
                  ))}
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  )
}
